import axios from "axios";
import template from "lodash.template";
import groupBy from "lodash.groupby";
import {
	PlayerBatStats,
	PlayerBatStatsZ,
	PlayerBowlStats,
	PlayerBowlStatsZ,
	PlayerFieldStats,
	PlayerFieldStatsZ,
	SeasonZ,
	Team,
	TeamsZ
} from "../schema/schema";
axios.defaults.baseURL = "https://grassrootsapiproxy.cricket.com.au";
/* eslint-disable no-template-curly-in-string */

export const getSeasons = async (orgId: string) => {
	const seasonsURLTemplate = template("/fixturesladders/organisations/${orgId}/seasons");
	const seasonsURL = seasonsURLTemplate({ orgId });
	const seasons = await axios.get(seasonsURL);
	return SeasonZ.array().parse(seasons.data.Seasons);
};

export const getTeamGradeIds = async (orgId: string, seasonId: string) => {
	const teamsURL = `/fixturesladders/organisations/${orgId}/teams?seasonId=${seasonId}`;
	const res = await axios.get(teamsURL);
	const teams = TeamsZ.parse(res.data);
	const teamData = await Promise.all(teams.Teams);
	return teamData;
};

type Stats = {
	teamName: string;
	batStats: PlayerBatStats[];
	bowlStats: PlayerBowlStats[];
	fieldStats: PlayerFieldStats[];
};

export const getStats = async (gradeId: string, teamId: string): Promise<Stats> => {
	const teamStatsUrlTemplate = template(
		"/participants/grades/${grade}/${type}-statistics?teamId=${team}"
	);
	const batStatsUrl = teamStatsUrlTemplate({
		grade: gradeId,
		team: teamId,
		type: "batting"
	});
	const bowlStatsUrl = teamStatsUrlTemplate({
		grade: gradeId,
		team: teamId,
		type: "bowling"
	});
	const fieldStatsUrl = teamStatsUrlTemplate({
		grade: gradeId,
		team: teamId,
		type: "fielding"
	});

	const batStats = await axios.get(batStatsUrl);
	const bowlStats = await axios.get(bowlStatsUrl);
	const fieldStats = await axios.get(fieldStatsUrl);

	return {
		teamName: teamId,
		batStats: PlayerBatStatsZ.array().parse(batStats.data),
		bowlStats: PlayerBowlStatsZ.array().parse(bowlStats.data),
		fieldStats: PlayerFieldStatsZ.array().parse(fieldStats.data)
	};
};

export const collectStats = async (teams: Team[]) => {
	const teamsStore: Promise<Stats>[] = [];

	teams.forEach(team => {
		team.Grades.filter(g => g.IsCurrent).forEach(async grade => {
			const stats = getStats(grade.Id, team.Id);
			teamsStore.push(stats);
		});
	});

	const awaitedTeamStats = await Promise.all(teamsStore);
	return awaitedTeamStats;
};

export const combineBatStats = (teamStats: Stats[]): PlayerBatStats[] => {
	const batStats = teamStats
		.map(stats => stats.batStats)
		.reduce((acc, statsList) => [...acc, ...statsList], []);

	const groupedPlayers = groupBy(batStats, "Id");

	const playersAggregates = Object.values(groupedPlayers).map(statsList => {
		if (statsList.length === 1) {
			return statsList[0];
		} else {
			const combined: PlayerBatStats = {
				Id: statsList[0].Id,
				Name: statsList[0].Name,
				ShortName: statsList[0].ShortName,
				Statistics: {
					Batting0s: 0,
					Batting100s: 0,
					Batting50s: 0,
					BattingAggregate: 0,
					BattingBallsFaced: 0,
					BattingFours: 0,
					BattingHighScore: 0,
					BattingInnings: 0,
					BattingNotOuts: 0,
					BattingSixes: 0,
					IsBattingHSNotOut: false,
					Matches: 0
				}
			};
			statsList.forEach(({ Statistics }) => {
				combined.Statistics.BattingInnings += Statistics.BattingInnings;
				combined.Statistics.Matches += Statistics.Matches;
				combined.Statistics.BattingNotOuts += Statistics.BattingNotOuts;
				combined.Statistics.Batting0s += Statistics.Batting0s;
				combined.Statistics.Batting50s += Statistics.Batting50s;
				combined.Statistics.Batting100s += Statistics.Batting100s;
				combined.Statistics.BattingAggregate += Statistics.BattingAggregate;
				combined.Statistics.BattingBallsFaced += Statistics.BattingBallsFaced;
				combined.Statistics.BattingSixes += Statistics.BattingSixes;
				combined.Statistics.BattingFours += Statistics.BattingFours;
				combined.Statistics.BattingHighScore =
					Statistics.BattingHighScore > combined.Statistics.BattingHighScore
						? Statistics.BattingHighScore
						: combined.Statistics.BattingHighScore;
				combined.Statistics.IsBattingHSNotOut =
					Statistics.BattingHighScore > combined.Statistics.BattingHighScore
						? Statistics.IsBattingHSNotOut
						: combined.Statistics.IsBattingHSNotOut;
			});

			return combined;
		}
	});
	return playersAggregates;
};

export const combineBowlStats = (teamStats: Stats[]): PlayerBowlStats[] => {
	const bowlStats = teamStats
		.map(stats => stats.bowlStats)
		.reduce((acc, statsList) => [...acc, ...statsList], []);

	const groupedPlayers = groupBy(bowlStats, "Id");

	const playersAggregates = Object.values(groupedPlayers).map(statsList => {
		if (statsList.length === 1) {
			return statsList[0];
		} else {
			const combined: PlayerBowlStats = {
				Id: statsList[0].Id,
				Name: statsList[0].Name,
				ShortName: statsList[0].ShortName,
				Statistics: {
					Matches: 0,
					BowlingBalls: 0,
					BowlingRuns: 0,
					BowlingWickets: 0,
					BowlingMaidens: 0,
					Bowling5WIs: 0,
					Bowling10WMs: 0,
					BowlingNoBalls: 0,
					BowlingWides: 0,
					BowlingBestInnings: ""
				}
			};
			statsList.forEach(({ Statistics }) => {
				combined.Statistics.Matches += Statistics.Matches;
				combined.Statistics.BowlingBalls += Statistics.BowlingBalls;
				combined.Statistics.BowlingRuns += Statistics.BowlingRuns;
				combined.Statistics.BowlingWickets += Statistics.BowlingWickets;
				combined.Statistics.BowlingMaidens += Statistics.BowlingMaidens;
				combined.Statistics.Bowling5WIs += Statistics.Bowling5WIs;
				combined.Statistics.Bowling10WMs += Statistics.Bowling10WMs;
				combined.Statistics.BowlingNoBalls += Statistics.BowlingNoBalls;
				combined.Statistics.BowlingWides += Statistics.BowlingWides;

				const [thisBestWktsRaw, thisBestRunsRaw] = Statistics.BowlingBestInnings.split("-");
				const [combinedBestWktsRaw, combinedBestRunsRaw] =
					combined.Statistics.BowlingBestInnings.split("-");

				const thisBestWkts = Number(thisBestWktsRaw || 0);
				const thisBestRuns = Number(thisBestRunsRaw || Number.MAX_SAFE_INTEGER);
				const combinedBestWkts = Number(combinedBestWktsRaw || 0);
				const combinedBestRuns = Number(combinedBestRunsRaw || Number.MAX_SAFE_INTEGER);

				combined.Statistics.BowlingBestInnings =
					thisBestWkts > combinedBestWkts
						? Statistics.BowlingBestInnings
						: thisBestWkts === combinedBestWkts
							? thisBestRuns < combinedBestRuns
								? Statistics.BowlingBestInnings
								: combined.Statistics.BowlingBestInnings
							: combined.Statistics.BowlingBestInnings;
			});

			return combined;
		}
	});
	return playersAggregates;
};

export const combineFieldStats = (teamStats: Stats[]): PlayerFieldStats[] => {
	const fieldStats = teamStats
		.map(stats => stats.fieldStats)
		.reduce((acc, statsList) => [...acc, ...statsList], []);

	const groupedPlayers = groupBy(fieldStats, "Id");

	const playersAggregates = Object.values(groupedPlayers).map(statsList => {
		if (statsList.length === 1) {
			return statsList[0];
		} else {
			const combined: PlayerFieldStats = {
				Id: statsList[0].Id,
				Name: statsList[0].Name,
				ShortName: statsList[0].ShortName,
				Statistics: {
					FieldingAssistedRunOuts: 0,
					FieldingCatchesNonWK: 0,
					FieldingCatchesWK: 0,
					FieldingRunOuts: 0,
					FieldingStumpings: 0,
					FieldingTotalCatches: 0,
					FieldingUnassistedRunOuts: 0,
					Matches: 0
				}
			};
			statsList.forEach(({ Statistics }) => {
				combined.Statistics.Matches += Statistics.Matches;
				combined.Statistics.FieldingAssistedRunOuts += Statistics.FieldingAssistedRunOuts;
				combined.Statistics.FieldingCatchesNonWK += Statistics.FieldingCatchesNonWK;
				combined.Statistics.FieldingCatchesWK += Statistics.FieldingCatchesWK;
				combined.Statistics.FieldingRunOuts += Statistics.FieldingRunOuts;
				combined.Statistics.FieldingStumpings += Statistics.FieldingStumpings;
				combined.Statistics.FieldingTotalCatches += Statistics.FieldingTotalCatches;
				combined.Statistics.FieldingUnassistedRunOuts += Statistics.FieldingUnassistedRunOuts;
			});

			return combined;
		}
	});
	return playersAggregates;
};
